import React, { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'

import Layout from '@components/layout'
import { getAllProductSlugs, getProduct } from '@lib/api'
import { formatPrice, hasObject } from '@lib/helpers'

import { Module } from '@modules/index'
import { websiteLocales } from '@lib/routes'
import Accordions from '@blocks/accordions'
import { toPlainText } from 'studio/components/previews/seo/frontend-utils'
import { imageUrlFor } from '@components/photo'

// setup our activeVariant hook
function useActiveVariantForRoute({ fallback, variants }) {
  const router = useRouter()
  const queryID = parseInt(router?.query?.variant)
  const hasVariant = variants.find((v) => v.id === queryID)
  const activeVariantId = hasVariant ? queryID : fallback
  const setActiveVariant = useCallback(
    (variant) => {
      router.replace(
        `/products/${router?.query?.slug}?variant=${variant}`,
        undefined,
        {
          shallow: true,
        }
      )
    },
    [router]
  )

  return [activeVariantId, setActiveVariant]
}

const Product = ({ data }) => {
  const router = useRouter()

  // extract our data
  const { site, page } = data

  // set our Product state
  const [product, setProduct] = useState(page.product)

  // find the default variant for this product by matching against the first product option
  const defaultVariant = page.product.variants?.find((v) => {
    const option = {
      name: page.product.options?.[0]?.name,
      value: page.product.options?.[0]?.values[0],
      position: page.product.options?.[0]?.position,
    }
    return hasObject(v.options, option)
  })
  // set our activeVariant state to our defaultVariant (if found) or first variant
  const [activeVariantId, setActiveVariant] = useActiveVariantForRoute({
    fallback: defaultVariant?.id || page.product.variants[0].id,
    variants: page.product.variants,
  })

  return (
    <>
      {!router.isFallback && (
        <Layout
          site={site}
          page={page}
          schema={getProductSchema(product, activeVariantId, site)}
        >
          {page.modules?.map((module, key) => (
            <Module
              key={key}
              uniqueId={key}
              module={module}
              product={product}
              activeVariantId={activeVariantId}
              onVariantChange={setActiveVariant}
            />
          ))}

          {((product.globalProductInfo && product.globalProductInfo.length) ||
            product.sharedProductInfo) && (
            <div className="product--shared-info">
              {product.sharedProductInfo && (
                <Accordions data={{ items: product.sharedProductInfo }} />
              )}
              {product.globalProductInfo && (
                <Accordions data={{ items: product.globalProductInfo }} />
              )}
            </div>
          )}
        </Layout>
      )}
    </>
  )
}

const getTomorrowsDate = () => {
  const today = new Date()
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  return tomorrow.toISOString().slice(0, 10)
}

function getProductSchema(product, activeVariant, site) {
  if (!product) return null

  const router = useRouter()
  const { query } = router

  const variant = product.variants.find((v) => v.id == activeVariant)

  // find the first gallery photo that actually has a visible image
  const mainPhoto = product.photos?.main?.find((set) => set?.photos?.[0])
    ?.photos?.[0]

  return {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: product.title,
    description: toPlainText(product.shortDescription || product.description),
    category: product.productCategory,
    ...(mainPhoto && {
      image: imageUrlFor(mainPhoto).width(1800).url(),
    }),
    sku: query.variant ? variant.sku : product.sku,
    offers: {
      '@type': 'Offer',
      url: `${process.env.CANONICAL_URL}/products/${product.slug}${
        query.variant ? `?variant=${variant.id}` : ''
      }`,
      itemCondition: 'http://schema.org/NewCondition',
      availability: query.variant
        ? `http://schema.org/${variant.inStock ? 'InStock' : 'SoldOut'}`
        : `http://schema.org/${product.inStock ? 'InStock' : 'SoldOut'}`,
      price: query.variant ? variant.price : product.price,
      priceCurrency: 'JPY',
      priceValidUntil: getTomorrowsDate(),
    },
    brand: {
      '@type': 'Brand',
      name: product.brand?.name || site.seo.siteTitle,
    },
  }
}

export async function getStaticProps({ params, preview, previewData, locale }) {
  const pageData = await getProduct({
    slug: params.slug,
    preview: {
      active: preview,
      token: previewData?.token,
    },
    locale,
  })

  const notFound = !pageData.page

  return {
    props: {
      data: pageData,
    },
    revalidate: 60,
    notFound,
  }
}

export async function getStaticPaths() {
  const allProducts = await getAllProductSlugs('product')

  return {
    paths:
      allProducts
        ?.map((page) => {
          return websiteLocales.map((locale) => ({
            params: {
              slug: page.slug,
            },
            locale,
          }))
        })
        .flat() || [],
    fallback: 'blocking',
  }
}

export default Product
